<template>
  <div class="home-page">
    <section class="hero-section">
      <div class="hero-card">
        <span class="hero-eyebrow">Free ACME certificates</span>
        <h1 class="hero-title">SSL certificates in three plain steps</h1>
        <p class="hero-sub">
          This service drives <span class="highlight" @click="goLetsEncrypt">Let's Encrypt</span> for you:
          create an order, publish one DNS TXT record, verify — then download the certificate.
          No command line, no account juggling.
        </p>
        <div class="hero-buttons">
          <el-button type="primary" v-if="status==='anon'" @click="goLogin" round size="large">
            <i class="el-icon-right"></i> Get Started
          </el-button>
          <el-button type="primary" v-if="status==='authed'" @click="goUser" round size="large">
            <i class="el-icon-data-analysis"></i> Go to Dashboard
          </el-button>
          <el-button type="info" @click="goDonate" round size="large">
            <i class="el-icon-coin"></i> Donate
          </el-button>
        </div>
      </div>
    </section>

    <section class="flow" ref="flow">
      <header class="flow-head">
        <span class="hero-eyebrow">How it works</span>
        <h2 class="flow-title">Three steps, one TXT record each</h2>
        <p class="flow-sub">
          Nothing to install: this service talks to Let's Encrypt for you and tells you exactly
          which record to publish where.
        </p>
      </header>

      <ol class="flow-list">
        <li class="flow-item">
          <div class="flow-rail"><span class="flow-dot">1</span></div>
          <div class="flow-card">
            <h3 class="flow-heading">Create the order</h3>
            <p class="flow-text">
              Enter your domain and choose whether every subdomain should be covered.
              We register the order with Let's Encrypt and hand you the exact TXT record to publish.
            </p>
            <ul class="flow-points">
              <li><i class="el-icon-check"></i> One order per certificate — never a reused authorization</li>
              <li><i class="el-icon-check"></i> Both records (apex/www or wildcard) are created up front</li>
            </ul>
          </div>
        </li>
        <li class="flow-item">
          <div class="flow-rail"><span class="flow-dot">2</span></div>
          <div class="flow-card">
            <h3 class="flow-heading">Publish the DNS records</h3>
            <p class="flow-text">
              Copy the record name and value into your DNS provider. They have to resolve from the
              public internet before Let's Encrypt can look them up.
            </p>
            <ul class="flow-points">
              <li><i class="el-icon-check"></i> One click copies the record name or the value</li>
              <li><i class="el-icon-check"></i> “Check DNS” reads the authoritative nameserver, not a cache</li>
            </ul>
          </div>
        </li>
        <li class="flow-item">
          <div class="flow-rail"><span class="flow-dot">3</span></div>
          <div class="flow-card">
            <h3 class="flow-heading">Verify and download</h3>
            <p class="flow-text">
              Press Verify — the check runs in the background with live progress, so you can leave
              the page. When it passes, download the certificate and its private key as a zip.
            </p>
            <ul class="flow-points">
              <li><i class="el-icon-check"></i> Progress and results stay on the page, never a blocking overlay</li>
              <li><i class="el-icon-check"></i> The certificate is valid for 90 days; renew with a new order</li>
            </ul>
          </div>
        </li>
      </ol>
    </section>

    <div class="acknowledgement-inline">
      <div class="ack-content">
        <div class="ack-section">
          <span class="ack-text">Powered by</span>
          <img src="../assets/letsencrypt-logo.png" alt="letsencrypt-logo" @click="goLetsEncrypt" class="ack-logo-inline"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { session } from "@/util/session";

export default {
  name: 'Home',
  data() {
    return { observer: null };
  },
  computed: {
    // Same source as the header: the cookie check decides which call to action makes sense
    status() {
      return session.status;
    },
  },
  mounted() {
    this.setupScrollReveal();
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    // Scroll reveal on vertical scroll: show on entering the viewport, keep it after scrolling past (unobserve, never replay)
    setupScrollReveal() {
      const list = this.$el.querySelector('.flow-list');
      const items = list ? [...list.querySelectorAll('.flow-item')] : [];
      if (!items.length) return;
      if (!('IntersectionObserver' in window)) return;   // stay visible by default when unsupported
      list.classList.add('is-animatable');
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          this.observer.unobserve(entry.target);
        });
      }, { threshold: 0.2, rootMargin: '0px 0px -8% 0px' });
      items.forEach((el) => this.observer.observe(el));
    },
    goLetsEncrypt() {
      window.open("https://letsencrypt.org/")
    },
    goDonate(){
      this.$router.push({path: '/donate'});
    },
    goLogin() {
      if (this.$route.path !== '/login') {
        this.$router.push({path: '/login'});
      }
    },
    goUser() {
      if (this.$route.path !== '/dashboard') {
        this.$router.push({path: '/dashboard'});
      }
    },
  }
}
</script>
<style lang="scss" scoped>
.home-page {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 28px 20px 48px;
}

/* ── Hero: brand gradient banner ───────────────────────────── */
.hero-section {
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
}

.hero-card {
  position: relative;
  overflow: hidden;
  padding: 44px 40px 46px;
  color: #fff;
  background: var(--brand-grad);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-hero);
  animation: heroSlideUp 0.5s ease-out;

  &::after {
    content: '';
    position: absolute;
    right: -80px;
    top: -140px;
    width: 380px;
    height: 380px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }

  > * {
    position: relative;
    z-index: 1;
  }
}

@keyframes heroSlideUp {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #fff;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 999px;
}

.hero-title {
  max-width: 640px;
  margin: 18px 0 14px;
  font-size: 38px;
  font-weight: 700;
  line-height: 1.18;
  letter-spacing: -0.02em;
}

.hero-sub {
  max-width: 620px;
  margin: 0;
  font-size: 15px;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.86);
}

.highlight {
  font-weight: 700;
  color: #fff;
  text-decoration: underline;
  text-decoration-color: rgba(255, 255, 255, 0.5);
  text-underline-offset: 3px;
  cursor: pointer;

  &:hover {
    text-decoration-color: #fff;
  }
}

.hero-buttons {
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 26px;

  .el-button {
    padding: 13px 26px;
    font-size: 15px;
    font-weight: 600;
  }

  // On the gradient background: primary button gets a white fill, secondary a translucent white
  .el-button--primary {
    color: var(--brand-1);
    background: #fff;
    border-color: #fff;
    box-shadow: 0 10px 24px rgba(30, 27, 75, 0.22);
  }

  .el-button--primary:not(.is-disabled):hover,
  .el-button--primary:not(.is-disabled):focus {
    color: var(--brand-2);
    background: #fff;
    border-color: #fff;
  }

  .el-button--info {
    color: #fff;
    background: rgba(255, 255, 255, 0.16);
    border-color: rgba(255, 255, 255, 0.42);
    box-shadow: none;
  }

  .el-button--info:not(.is-disabled):hover,
  .el-button--info:not(.is-disabled):focus {
    color: #fff;
    background: rgba(255, 255, 255, 0.26);
    border-color: #fff;
  }
}

/* ── Three steps: vertical layout + scroll reveal ─────────────────────── */
.flow {
  width: 100%;
  max-width: 1140px;
  margin: 34px auto 0;
}

.flow-head {
  max-width: 620px;
  margin-bottom: 26px;

  .hero-eyebrow {
    color: var(--brand-1);
    background: var(--brand-grad-soft);
    border-color: transparent;
  }
}

.flow-title {
  margin: 14px 0 8px;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--text);
}

.flow-sub {
  margin: 0;
  font-size: 14px;
  line-height: 1.65;
  color: var(--text-muted);
}

.flow-list {
  position: relative;
  list-style: none;
  margin: 0;
  padding: 0;
}

/* Vertical rail: one gradient line from top to bottom tying the three steps together */
.flow-list::before {
  content: '';
  position: absolute;
  left: 21px;
  top: 18px;
  bottom: 18px;
  width: 2px;
  background: linear-gradient(180deg, var(--brand-1) 0%, var(--brand-2) 55%, var(--border) 100%);
  opacity: 0.45;
}

.flow-item {
  display: flex;
  gap: 18px;
  padding-bottom: 20px;

  &:last-child {
    padding-bottom: 0;
  }
}

.flow-rail {
  position: relative;
  flex: none;
  width: 44px;
  display: flex;
  justify-content: center;
}

.flow-dot {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  margin-top: 14px;
  font-size: 14px;
  font-weight: 700;
  color: var(--text-faint);
  background: #fff;
  border: 2px solid var(--border);
  border-radius: 50%;
  transition: color 0.3s ease, background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
}

.flow-card {
  flex: 1;
  min-width: 0;
  padding: 20px 22px 22px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
}

.flow-heading {
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 700;
  color: var(--text);
}

.flow-text {
  margin: 0 0 14px;
  font-size: 13.5px;
  line-height: 1.7;
  color: var(--text-muted);
}

.flow-points {
  list-style: none;
  margin: 0;
  padding: 14px 0 0;
  border-top: 1px dashed var(--border);
  display: grid;
  gap: 8px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 13px;
    line-height: 1.6;
    color: var(--text);
  }

  i {
    flex: none;
    margin-top: 2px;
    font-size: 12px;
    color: var(--ok);
  }
}

/* Scroll reveal: hidden only when IntersectionObserver is supported, so a JS failure still shows the content */
.flow-list.is-animatable .flow-item {
  opacity: 0;
  transform: translateY(22px);
  transition: opacity 0.5s ease, transform 0.5s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.flow-list.is-animatable .flow-item.is-visible {
  opacity: 1;
  transform: none;
}

.flow-list.is-animatable .flow-item.is-visible .flow-dot {
  color: #fff;
  border-color: transparent;
  background: var(--brand-grad);
  box-shadow: 0 6px 14px rgba(99, 102, 241, 0.32);
}

@media screen and (max-width: 992px) {
  .flow-list::before {
    left: 17px;
  }

  .flow-rail {
    width: 36px;
  }

  .flow-dot {
    width: 28px;
    height: 28px;
    font-size: 12px;
    margin-top: 14px;
  }
}

.acknowledgement-inline {
  width: 100%;
  max-width: 1140px;
  margin: 34px auto 0;
}

.ack-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

.ack-section {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 999px;
}

.ack-text {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-muted);
}

.ack-logo-inline {
  height: 22px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.75;
  }
}

@media screen and (max-width: 768px) {
  .home-page {
    padding: 16px 12px 32px;
  }

  .hero-card {
    padding: 30px 22px 32px;
  }

  .hero-title {
    font-size: 27px;
  }

  .hero-sub {
    font-size: 14px;
  }

  .hero-buttons {
    gap: 10px;

    .el-button {
      padding: 12px 20px;
      font-size: 14px;
    }
  }

  .flow {
    margin-top: 26px;
  }

  .flow-title {
    font-size: 20px;
  }

  .flow-card {
    padding: 16px 16px 18px;
  }

  .flow-item {
    gap: 12px;
    padding-bottom: 14px;
  }
}

@media screen and (max-width: 480px) {
  .hero-title {
    font-size: 24px;
  }

  .hero-buttons {
    flex-direction: column;
    align-items: stretch;

    .el-button {
      width: 100%;
    }
  }
}
</style>
